import { Injectable } from '@angular/core';
import { TranslateStore, InterpolatableTranslation, isDefinedAndNotNull, isDefined, isDict, isArray } from '@ngx-translate/core';

/**
 * Custom TranslateStore that uses '|' as the key separator instead of '.'
 * This is needed because IC COMPLEX uses '|' for translation namespaces (e.g., 'INTERFACE|Dodaj')
 */
@Injectable()
export class IccTranslateStore extends TranslateStore {

    /**
     * Retrieves a value from a nested object using a pipe-separated key path.
     * Overrides the default behavior which uses dot ('.') as separator.
     *
     * Example usage:
     * ```ts
     * getValue({ 'INTERFACE|Dodaj': 'Add' }, 'INTERFACE|Dodaj'); // returns 'Add'
     * ```
     *
     * @param language The language to get translation for.
     * @param key Pipe-separated key path specifying the value to retrieve.
     * @returns The value at the specified key path, or `undefined` if not found.
     */
    protected override getValue(language: string, key: string): InterpolatableTranslation {
        const translations = this.getTranslations(language);
        return this.getValueWithPipeSeparator(translations, key);
    }

    /**
     * Custom getValue implementation using '|' as separator instead of '.'
     */
    private getValueWithPipeSeparator(target: unknown, key: string): InterpolatableTranslation {
        const keys = key.split('|');
        let currentKey = '';

        do {
            currentKey += keys.shift();
            const isLastKey = !keys.length;

            if (isDefinedAndNotNull(target)) {
                if (
                    isDict(target)
                    && isDefined((target as Record<string, unknown>)[currentKey])
                    && (isDict((target as Record<string, unknown>)[currentKey])
                        || isArray((target as Record<string, unknown>)[currentKey])
                        || isLastKey)
                ) {
                    target = (target as Record<string, unknown>)[currentKey];
                    currentKey = '';
                    continue;
                }

                if (isArray(target)) {
                    const index = parseInt(currentKey, 10);
                    if (
                        isDefined((target as unknown[])[index])
                        && (isDict((target as unknown[])[index])
                            || isArray((target as unknown[])[index])
                            || isLastKey)
                    ) {
                        target = (target as unknown[])[index];
                        currentKey = '';
                        continue;
                    }
                }
            }

            if (isLastKey) {
                target = undefined;
                continue;
            }
            currentKey += '|';
        } while (keys.length);

        return target as InterpolatableTranslation;
    }
}
